import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { toast } from "react-toastify";
import { StandardURL } from "../App";

const ReservationsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loadingReminder, setLoadingReminder] = useState(false);
  const [loadingChangeStatus, setLoadingChangeStatus] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const propertyId = localStorage.getItem("selectedPropertyId");
    const token = localStorage.getItem("token");

    if (propertyId && token) {
      axios
        .get(
          `${StandardURL}/iNyumba/user/reservations/viewAll/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setReservations(response.data.response);
        })
        .catch((error) => {
          console.error("Error fetching reservations:", error);
        });
    }
  }, []);

  const handleViewBills = (roomId) => {
    localStorage.setItem("selectedRentalId", roomId);
    navigate("/landlord/bills");
  };

  const handleSendEvictionNotice = (userId) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to continue? This will send the eviction notice."
    );

    if (isConfirmed) {
      const token = localStorage.getItem("token");

      if (token) {
        axios
          .post(
            `${StandardURL}/iNyumba/admin/noticelandlordtotenant/${userId}`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((response) => {
            console.log("Eviction notice sent successfully", response.data);
            alert("Eviction notice sent successfully!");
          })
          .catch((error) => {
            console.error("Error sending eviction notice:", error);
            alert("Failed to send eviction notice.");
          });
      }
    }
  };

  const handleMpesaPayment = (userId, phoneNumber) => {
    const token = localStorage.getItem("token");

    if (!phoneNumber) {
      toast.error("Please enter a phone number");
      return;
    }

    axios
      .post(
        `${StandardURL}/iNyumba/user/mpesapayment/rentpaymentinitiatestk/${userId}`,
        { phoneNumber },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        setShowPhoneModal(false);
        setPhoneNumber("");
      })
      .catch((error) => {
        console.error("Error initiating mpesa payment:", error);
        toast.error(error.response?.data?.response || "STK push failed");
        setShowPhoneModal(false);
      });
  };

  const handleSendReminder = (type) => {
    const propertyId = localStorage.getItem("selectedPropertyId");
    const token = localStorage.getItem("token");
    if (!propertyId || !token) return;

    setLoadingReminder(true);
    const url =
      type === "email"
        ? `${StandardURL}/iNyumba/sendbillremindersemail/${propertyId}`
        : `${StandardURL}/iNyumba/sendbillreminderssms/${propertyId}`;

    axios
      .post(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        toast.success(res.data.response);
      })
      .catch((err) => {
        toast.error(err.response?.data?.response || "Failed to send reminder");
      })
      .finally(() => setLoadingReminder(false));
  };

  const handleChangeStatus = (userId) => {
    setSelectedUserId(userId);
    setShowChangeStatusModal(true);
  };

  const confirmChangeStatus = () => {
    const token = localStorage.getItem("token");
    if (!selectedUserId || !token) return;

    setLoadingChangeStatus(true);

    axios
      .patch(
        `${StandardURL}/iNyumba/updatereservationstatusmanually/${selectedUserId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        toast.success("Status updated successfully!");
        setReservations((prev) =>
          prev.map((r) =>
            r.user_id === selectedUserId
              ? { ...r, payment_status: "success" }
              : r
          )
        );
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.response || "Failed to update reservation status"
        );
      })
      .finally(() => {
        setLoadingChangeStatus(false);
        setShowChangeStatusModal(false);
        setSelectedUserId(null);
      });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
      case "active":
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
      case "evicted":
      case "unpaid":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {/* Top buttons */}
            <div className="flex space-x-4 mb-6">
              <button
                onClick={() => handleSendReminder("email")}
                disabled={loadingReminder}
                className={`px-4 py-2 rounded-md text-white font-semibold ${
                  loadingReminder
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {loadingReminder ? "Sending..." : "Reminder via Email"}
              </button>
              <button
                onClick={() => handleSendReminder("sms")}
                disabled={loadingReminder}
                className={`px-4 py-2 rounded-md text-white font-semibold ${
                  loadingReminder
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {loadingReminder ? "Sending..." : "Reminder via SMS"}
              </button>
            </div>

            <div className="flex justify-between items-center mb-6 sm:mb-8">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">
                Reservations & Tenants 🔑
              </h1>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700"
              >
                Go Back
              </button>
            </div>

            {reservations.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {reservations.map((reservation) => (
                  <div
                    key={reservation.room_id}
                    className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 flex flex-col justify-between"
                  >
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                        Tenant: {reservation.user_name}
                        {reservation.house_no && (
                          <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                            House {reservation.house_no}
                          </span>
                        )}
                      </h3>
                      <div className="space-y-2 text-gray-700 text-sm">
                        <p>
                          <strong>Email:</strong> {reservation.email}
                        </p>
                        <p>
                          <strong>Phone:</strong> {reservation.phone}
                        </p>
                        <p>
                          <strong>Entry Date:</strong> {reservation.entrydate}
                        </p>
                        <p>
                          <strong>Booking Status:</strong>{" "}
                          <span
                            className={`px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(
                              reservation.booking_status
                            )}`}
                          >
                            {reservation.booking_status}
                          </span>
                        </p>
                        <p>
                          <strong>Payment Status:</strong>{" "}
                          <span
                            className={`px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(
                              reservation.payment_status
                            )}`}
                          >
                            {reservation.payment_status}
                          </span>
                        </p>
                        <p>
                          <strong>Deposit Paid:</strong>{" "}
                          <span className="text-green-700 font-medium">
                            {reservation.deposit_paid}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col space-y-3">
                      <button
                        onClick={() => handleViewBills(reservation.room_id)}
                        disabled={
                          reservation.payment_status?.toLowerCase() === "pending"
                        }
                        className={`w-full px-4 py-2 text-white font-semibold rounded-lg shadow-md ${
                          reservation.payment_status?.toLowerCase() === "pending"
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                        }`}
                      >
                        Set Tenant Bills
                      </button>

                      {reservation.payment_status?.toLowerCase() ===
                        "pending" && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedUserId(reservation.user_id);
                              setShowPhoneModal(true);
                            }}
                            className="w-full px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700"
                          >
                            Send STK Push
                          </button>

                          <button
                            onClick={() => handleChangeStatus(reservation.user_id)}
                            className="w-full px-4 py-2 bg-yellow-600 text-white font-semibold rounded-lg shadow-md hover:bg-yellow-700"
                          >
                            Change Status
                          </button>
                        </>
                      )}

                      <button
                        onClick={() =>
                          handleSendEvictionNotice(reservation.user_id)
                        }
                        className="w-full px-4 py-2 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700"
                      >
                        Send Eviction Notice
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
                <p className="text-xl text-gray-700 font-medium mb-4">
                  No reservations found at the moment. 😔
                </p>
                <p className="text-md text-gray-500">
                  When new reservations are made, they will appear here.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Phone Modal */}
        {showPhoneModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96">
              <h2 className="text-lg font-semibold mb-4">Enter Phone Number</h2>
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter phone number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowPhoneModal(false)}
                  className="px-4 py-2 bg-gray-400 text-white rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleMpesaPayment(selectedUserId, phoneNumber)
                  }
                  className="px-4 py-2 bg-green-600 text-white rounded-lg"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Change Status Modal */}
        {showChangeStatusModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96">
              <h2 className="text-lg font-semibold mb-4">
                Are you sure you want to change status?
              </h2>
              <p className="mb-4 text-gray-600">
                Clicking <strong>Yes</strong> will delete the bill from the DB
                and update the reservation Payment status to{" "}
                <strong>Success</strong>.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowChangeStatusModal(false)}
                  className="px-4 py-2 bg-gray-400 text-white rounded-lg"
                >
                  No
                </button>
                <button
                  onClick={confirmChangeStatus}
                  disabled={loadingChangeStatus}
                  className={`px-4 py-2 text-white rounded-lg ${
                    loadingChangeStatus
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-yellow-600 hover:bg-yellow-700"
                  }`}
                >
                  {loadingChangeStatus ? "Processing..." : "Yes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </AuthGuard>
    </RoleGuard>
  );
};

export default ReservationsPage;
